"use client";
import {
  Image,
  Text,
  Flex,
  useDisclosure,
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  useToast,
  Show,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import Link from "next/link";
import { MinusIcon, AddIcon } from "@chakra-ui/icons";
import { useRouter, usePathname } from "next/navigation";
import { useDeleteModel, useGetAllModels } from "queries";
import { SkeletonCard, ModelsCreationsLayout } from "components";
import { useAppContext } from "hooks";
import { Model } from "types";
import { FaTrash } from "react-icons/fa";
import { POST_EXTEND_CKPT } from "api";
import { fetch } from "libs";
import { Metadata } from "next";

export const metadata: Metadata = {
  title: {
    absolute: "Free Public AI Models | DeepMode Character Gallery",
  },
  description:
    "Explore DeepMode's collection of free public AI models. Use these diverse characters in our AI art generator to create stunning digital art. Discover a world of AI-powered creativity accessible to all users.",
};

const Models = () => {
  const [pageSize] = useState(16);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const pathname = usePathname();
  const isUserModels = pathname === "/mymodels";
  const [sortType, setSortType] = useState<boolean>(true);
  const [filterType, setFilterType] = useState<string>("");

  // Fetch models with the hook
  const { data, fetchNextPage, hasNextPage, refetch } = useGetAllModels(
    isUserModels,
    pageSize,
    selectedTags,
    sortType,
    filterType
  );

  const toggleTag = (tag: string) => {
    setSelectedTags((prevTags) =>
      prevTags.includes(tag) ? prevTags.filter((t) => t !== tag) : [...prevTags, tag]
    );
  };

  const handleSortChange = (type: boolean) => {
    if (sortType === type) {
      setSortType(!type);
    } else {
      setSortType(type);
    }
  };

  const handleFilterChange = (type: string) => {
    if (filterType === type) {
      setFilterType("");
    } else {
      setFilterType(type);
    }
  };

  // Main rendering logic for the page
  return (
    <Flex direction="column">
      <h1
        style={{
          position: "absolute",
          left: "-9999px",
          top: "auto",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        Explore AI Character Models
      </h1>
      <ModelsCreationsLayout
        JumbotronTitle={pathname === "/mymodels" ? "My models" : "Models"}
        JumbotronSubtitle={
          pathname === "/mymodels" ? true : "Created by our community"
        }
        data={
          data ? data.pages.flatMap((page: any) =>
            page.models.filter((model: Model) => model.visible) // Filter models with visible = true
          )
            : Array(16)
                .fill(0)
                .map((_, index) => <SkeletonCard key={index} />)
        }
        renderCard={(model: Model) =>
          
          data ? (
            <ModelCard refetch={refetch} key={model.id} model={model} />
          ) : (
            <SkeletonCard />
          )
        }
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        selectedTags={selectedTags}
        toggleTag={toggleTag}
        onSortChange={isUserModels ? undefined : handleSortChange}
        onFilterChange={isUserModels ? undefined : handleFilterChange}
        sortType={isUserModels ? undefined : sortType}
        filterType={isUserModels ? undefined : filterType}
      />
      <CustomAccordion />
    </Flex>
  );
};

const ModelCard = ({
  model,
  refetch,
}: {
  model: Model;
  refetch: () => void;
}) => {
  console.log('Rendering ModelCard:', model.isFlux);

  const { profile, jwt } = useAppContext(); // Access profile and JWT for authentication
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isExtendOpen,
    onOpen: onExtendOpen,
    onClose: onExtendClose,
  } = useDisclosure();
  const pathname = usePathname();
  const isUserModels = pathname === "/mymodels"; // Check if it's the user's model page
  const basePath = isUserModels
    ? `/mymodels/${model.username}`
    : `public-models/${model.username}`;
  const publicPath = `/public-models/${model.username}`;
  const deleteModel = useDeleteModel();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expireInMilliSecs, setExpireInMilliSecs] = useState<number>(0);
  const router = useRouter();

  useEffect(() => {
    if (
      isUserModels &&
      model.checkpoints?.length &&
      model.checkpoints[0].expire_at !== null
    ) {
      setExpireInMilliSecs(
        new Date(model.checkpoints[0].expire_at).getTime() - new Date().getTime()
      );
    }
  }, [model]);

  const handleDelete = () => {
    deleteModel.mutate(
      { model_id: model.id, model_username: model.username },
      {
        onSuccess: () => {
          onClose();
          refetch();
        },
        onError: (error) => {
          console.error("Error deleting model:", error);
        },
      }
    );
  };

  const handleNavigation = (create: boolean, isExpired?: boolean | number) => {
    // Only check for checkpoints if it's the user's model page
    if (isUserModels && model && (!model.checkpoints || !model.checkpoints.length)) {
      return;
    }

    if (jwt) {
      if (create && !isExpired) {
        if (model.isFlux) {
          // Navigate to create-flux if isFlux is true
          router.push(`${basePath}/create-flux`);
        } else {
          router.push(`${basePath}/create`);
        }
      } else if (isExpired) {
        router.push(`/creations/${model.id}`);
      } else {
        router.push(publicPath);
      }
    } else {
      router.push(model.isFlux ? `${publicPath}/create-flux` : `${publicPath}/create`);
    }
  };

  const handleExtend = async () => {
    // Check if the user has enough credits to extend the model
    if (profile?.credit! < 20) {
      toast({
        title: "Not enough credit",
        description: "You don't have enough credit to extend a model",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Check if the model has checkpoints and if it's active or expired
    if (!model.checkpoints?.length || !model.checkpoints[0].active || expireInMilliSecs <= 0) {
      return;
    }

    try {
      setIsLoading(true); // Set loading state to true while processing

      // Send the request to extend the model storage using the checkpoint's file name
      const response = await fetch(POST_EXTEND_CKPT(model.checkpoints[0].file_name), {
        method: "POST",
      });

      refetch(); // Refetch models to refresh data after extending
      console.log("Extended checkpoint successfully", response);

      toast({
        title: "Success",
        description: "Extended model storage successfully",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error extending checkpoint:", error);

      // Show an error toast in case of failure
      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false); // Reset loading state after processing
      onExtendClose(); // Close the modal or any UI element related to the extend action
    }
  };

  const socialMediaName =
    model.social_media_accounts && model.social_media_accounts.length > 0
      ? model.social_media_accounts[0].name
      : "";

  return (
    <Flex>
      <Flex
        position="relative"
        _hover={{
          cursor: isUserModels
            ? model && (!model.checkpoints || !model.checkpoints.length)
              ? "not-allowed"
              : "pointer"
            : "pointer", // Ensure pointer cursor for public models
        }}
        onClick={() =>
          handleNavigation(
            isUserModels ? true : false,
            isUserModels &&
              model.checkpoints?.length &&
              model.checkpoints[0].expire_at !== null &&
              (!model.checkpoints[0].active || expireInMilliSecs <= 0)
          )
        }
        direction="column"
        w={{ base: "142px", md: "180px", lg: "264px" }}
      >
        <h1
          style={{
            position: "absolute",
            left: "-9999px",
            top: "auto",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
        >
          Explore AI Character Models
        </h1>
        {isUserModels &&
          model.checkpoints !== null &&
          model.checkpoints.length !== 0 && (
            <Button
              size="xs"
              colorScheme="red"
              position="absolute"
              transform="translateY(100%)"
              right={{ base: "2", lg: "4" }}
              top={{ base: "40%", md: "48%", lg: "58%" }}
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            >
              <Icon as={FaTrash} boxSize={4} />
            </Button>
          )}
        <Flex
          bg="black"
          borderRadius="32px"
          position="absolute"
          top={{ base: "2", lg: "4" }}
          right={{ base: "2", lg: "4" }}
          zIndex="1"
          p={{ base: "4px", lg: "8px" }}
          minW={{ base: "25px", md: "" }}
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Text fontWeight="semibold" fontSize="sm" color="white">
            {model.total_image_generations ?? 0}
          </Text>
          <Show above="md">
          <Text fontWeight="semibold" fontSize="sm" color="white" ml="4px">
          Creations
            </Text>
          </Show>
        </Flex>
        {model.isFlux ? (
          <Flex
            bg="#702EFF" // Background color for Flux
            borderRadius="32px"
            position="absolute"
            top={{ base: "2", lg: "4" }}
            left={{ base: "2", lg: "4" }}
            zIndex="1"
            p={{ base: "4px", lg: "8px" }}
            minW={{ base: "25px", md: "" }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Text fontWeight="semibold" fontSize="sm" color="white">
              ⚡
            </Text>
            <Show above="md">
              <Text fontWeight="semibold" fontSize="sm" color="white" ml="4px">
                Flux
              </Text>
            </Show>
          </Flex>
        ) : model.featured && (
          <Flex
            bg="black"
            borderRadius="32px"
            position="absolute"
            top={{ base: "2", lg: "4" }}
            left={{ base: "2", lg: "4" }}
            zIndex="1"
            p={{ base: "4px", lg: "8px" }}
            minW={{ base: "25px", md: "" }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Text fontWeight="semibold" fontSize="sm" color="white">
              ⭐
            </Text>
            <Show above="md">
              <Text fontWeight="semibold" fontSize="sm" color="white" ml="4px">
                Featured
              </Text>
            </Show>
          </Flex>
        )}

        <a
          onClick={(e) => e.preventDefault()}
          href={basePath}
          style={{ textDecoration: "none" }}
        >
          <Image
            boxShadow="0 0 5px 5px rgb(44, 49, 77)"
            borderRadius="14px"
            h={{ base: "142px", md: "180px", lg: "264px" }}
            w={{ base: "142px", md: "180px", lg: "264px" }}
            src={model.thumbnail}
            objectFit={"cover"}
          />
        </a>
        <Flex align={{ base: "top", lg: "center" }} mt="10px">
          <Flex ml="8px" direction="column">
            <Text fontWeight="semibold" fontSize="16px">
              {model.display_name}
            </Text>
            <Text fontSize="12px" color="gray">
              {socialMediaName}
            </Text>
          </Flex>
        </Flex>
        <Modal size="xl" isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent borderRadius={0} bg="#16181F" border="1px solid white">
            <ModalCloseButton />
            <Flex
              justify="center"
              w="100%"
              p={10}
              px={{ base: "30px", md: "80px" }}
              minH="300px"
              direction="column"
            >
              <Text fontSize={{ base: "28px", md: "36px" }} fontWeight={600}>
                Are you sure you want to delete this model?
              </Text>
              <Flex>
                <Button
                  mt="55px"
                  bg="white"
                  borderRadius="32px"
                  color="black"
                  onClick={handleDelete}
                >
                  Delete Model
                </Button>
              </Flex>
            </Flex>
          </ModalContent>
        </Modal>
        <Modal size="xl" isOpen={isExtendOpen} onClose={onExtendClose}>
          <ModalOverlay />
          <ModalContent borderRadius={0} bg="#16181F" border="1px solid white">
            <ModalCloseButton />
            <Flex
              justify="center"
              w="100%"
              p={10}
              px={{ base: "30px", md: "80px" }}
              minH="300px"
              direction="column"
            >
              <Text fontSize={{ base: "20px", md: "28px" }} fontWeight={500}>
                This will extend your model storage for 30-days.
              </Text>
              <Flex>
                <Button
                  bg={isLoading ? "gray" : "button.500"}
                  color="white"
                  w="100%"
                  minH="50px"
                  mt="55px"
                  fontSize={{ base: "12px", "2xl": "16px" }}
                  _hover={isLoading ? {} : { bg: "button.500", opacity: 0.9 }}
                  isDisabled={isLoading}
                  onClick={handleExtend}
                >
                  Extend (20 credits)
                </Button>
              </Flex>
            </Flex>
          </ModalContent>
        </Modal>
        <Flex mt="5px" justify="start">
          {isUserModels &&
          model.checkpoints?.length &&
          model.checkpoints[0].expire_at !== null &&
          (!model.checkpoints[0].active || expireInMilliSecs <= 0) ? (
            <Flex
              px="15px"
              py="3px"
              borderRadius="32px"
              bg="black"
              color="white"
              border="2px solid #2C314D"
              fontSize="12px"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <Text>EXPIRED</Text>
            </Flex>
          ) : (
            <Flex
              px="15px"
              py="3px"
              borderRadius="32px"
              bg="#FF1B6D"
              color="white"
              border="2px solid #2C314D"
              fontSize="12px"
              onClick={(event) => {
                event.stopPropagation();
                handleNavigation(true);
              }}
            >
              <Text>Create</Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};



const CustomAccordion = () => {
  const faqData = [
    {
      question: "What is DeepMode and how does it work?",
      answer: (
        <Text>
          DeepMode is a Generative AI platform that helps creators make their AI
          clones. Creators can generate beautiful AI photos, whether realistic
          or non-realistic, with unlimited freedom. With DeepMode, creators can
          unleash their imagination and create unique photos with unlimited
          freedom.
        </Text>
      ),
    },
    {
      question: "How much does DeepMode cost?",
      answer: (
        <Text>
          DeepMode offers various top-up plans: Check{" "}
          <Link style={{ textDecoration: "underline" }} href="/payments">
            this page
          </Link>{" "}
          for more details.
        </Text>
      ),
    },
    {
      question: "Can I try DeepMode for free?",
      answer: (
        <Text>
          Yes, every new user automatically gets 20 credits. However, to unlock
          training an AI model you will need to top-up your balance.
        </Text>
      ),
    },
    {
      question: "Are my DeepMode creations private?",
      answer: (
        <Text>
          Yes, your creations on DeepMode are kept private and only you can view
          them. You also have the option to delete specific models or images you
          don&apos;t like.
        </Text>
      ),
    },
    {
      question: "Does DeepMode store my uploaded images?",
      answer: (
        <Text>
          No, your uploaded photos are deleted immediately from our servers
          after training. In addition, the Model itself is being deleted from
          our servers after 30 days. If you wish to extend the model storage,
          you can do so.
        </Text>
      ),
    },
    {
      question: "Can I use my creations anywhere?",
      answer: (
        <Text>
          Yes. Your generated images are yours to use on any other platform.
        </Text>
      ),
    },
    {
      question:
        "How is DeepMode different from Midjourney, Stable Diffusion, or Dall-E?",
      answer: (
        <Text>
          Unlike other AI platforms such as Midjourney, Stable Diffusion, or
          DALL-E, DeepMode stands out for its exclusive focus on the generation
          of AI clones. DeepMode is committed to providing a fully uncensored
          experience, allowing users unlimited creative freedom without any
          content restrictions.
        </Text>
      ),
    },
  ];

  return (
    <Flex mt="20px" mb="20px" p={{ base: "20px", md: "100px" }} direction="column">
      <Text fontSize="40px" fontWeight="semibold">
        FAQs - Frequently Asked Questions
      </Text>
      <Accordion mt="10px" allowToggle>
        {faqData.map((item, index) => (
          <AccordionItem key={index}>
            {({ isExpanded }) => (
              <>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    {item.question}
                  </Box>
                  {isExpanded ? (
                    <MinusIcon fontSize="12px" />
                  ) : (
                    <AddIcon fontSize="12px" />
                  )}
                </AccordionButton>
                <AccordionPanel pb={4}>{item.answer}</AccordionPanel>
              </>
            )}
          </AccordionItem>
        ))}
      </Accordion>
    </Flex>
  );
};

export default Models;
